import React, { useEffect, useMemo } from 'react'
import { Box, TextField, Button, Grid, InputLabel, FormControl,
  Select, MenuItem
} from '@mui/material';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import DateAdapter from '@mui/lab/AdapterDateFns';
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from 'notistack';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import { saveEnquiry } from 'services/enquiries';
import { getDBDateFormat } from 'services/commonServices';
import { parse } from 'date-fns';

const AddEnquiry = ({ enquiryDetails = null, onEnquiryAdded }) => {
  const defaultFormFields = useMemo(() => ({
    name: '',
    mobileNumber: '',
    query: '',
    followUpDate: null,
    enquiryChannel: '',
    status: 'Open',
  }),[]);

  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, control, formState: { errors }, watch, reset } = useForm({
    mode: 'onTouched',
    defaultValues: {...defaultFormFields}
  });

  useEffect(() => {
    if(enquiryDetails){
      const data = {...(Object.fromEntries(Object.entries(defaultFormFields).map(([key, value]) => {
        return [key, enquiryDetails[key] || value];
      })))};      
      data['followUpDate'] = parse(enquiryDetails?.followUpDate, 'dd-MM-yyyy', new Date());
      data['status'] = enquiryDetails?.status || 'Open';
      reset({...data});
    }
  }, [enquiryDetails, defaultFormFields, reset]);

  
  const onSubmit = (data) => {
    saveEnquiry({
        ...data,
        followUpDate: getDBDateFormat(data.followUpDate),
        enquiryId: enquiryDetails?.enquiryId,
      }).then(({response, error}) => {
        if(response){
          enqueueSnackbar((`Enquiry ${enquiryDetails?.id ? 'updated' : 'added'} successfully`), {variant: 'success'});
          reset({...defaultFormFields});
          onEnquiryAdded();
        } else {
          enqueueSnackbar((response?.message || error), {variant: 'error'});
        }
      });
  }

  return (
    <Box noValidate component="form"  onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }} >
      <Grid container columnSpacing={2} rowSpacing={2} justifyContent="center">
        <Grid item xs={12} sm={12} md={10} lg={10}  >
          <Grid item container columnSpacing={2}>

            <Grid item xs={12} sm={12} md={12} lg={12} >
              <Controller
                control={control}
                name="name"
                id="name"
                rules={{ required: "Enter name" }}
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Name"
                    required
                    error={!!errors?.name}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Controller
                control={control}
                name="mobileNumber"
                id="mobileNumber"
                rules={{ required: "Enter mobile number",
                  pattern: {
                    value: /^[0-9]{10}$/i,
                    message: "Enter a valid 10 digit registered mobile number",
                  },
                }}
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Mobile Number"
                    required
                    error={!!errors?.mobileNumber}
                    helperText={errors.mobileNumber?.message}
                    size="small"
                    fullWidth
                    // InputProps={{
                    //   endAdornment: <InputAdornment position="start">%</InputAdornment>,
                    // }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Controller
                control={control}
                name="query"
                id="query"
                rules={{ required: "Enter query" }}
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Query"
                    required
                    error={!!errors?.query}
                    size="small"
                    fullWidth
                    multiline
                    rows="3"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={5} sx={{mt: 2}}>
              <Controller
                control={control}
                name="followUpDate"
                id="followUpDate"
                required
                rules={{ required: "Select payment date",
                  // validate: (value) => value?.getTime() >= new Date().getTime()  || "Date should be greater than today's date",
                  // validate: (value) => value?.getTime() >= watch('validityStartDate').getTime()  || "Validity End Date should be greater than Validity Start Date ",
                }}
                render={({field}) => (
                  <LocalizationProvider dateAdapter={DateAdapter} >
                    <DatePicker
                      {...field}
                      required
                      label="Follow up date"
                      inputFormat="dd/MM/yyyy"
                      name="followUpDate"
                      clearable={false}
                      // minDate={new Date()}
                      value={field.value}
                      onChange={field.onChange}
                      renderInput={(params) => (
                        <TextField 
                          required
                          value={field.value}
                          {...params} 
                          error={!!errors?.followUpDate}  
                          size="small" 
                          sx={{width: '100%'}} 
                          onKeyDown={(e) =>{ e.preventDefault(); return false;}}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} xl={7} lg={12} sx={{ mt: 2 }}>
              <Controller
                control={control}
                name="enquiryChannel"
                id="enquiryChannel"
                required
                rules={{ required: "Select enquiry channel" }}
                render={({field}) => (
                  <FormControl {...field} required fullWidth error={!!errors?.enquiryChannel}>
                    <InputLabel id="enquiryChannel-label" size="small">How did you hear about us?</InputLabel>
                    <Select
                      labelId="enquiryChannel-label"
                      id="enquiryChannel-select-id"
                      label="How did you hear about us?"
                      name="enquiryChannel"
                      required
                      size="small"
                      value={field.value}
                      onChange={field.onChange}
                      
                    >
                      <MenuItem value='Instagram/Facebook'>Instagram/Facebook</MenuItem>
                      <MenuItem value='Email/Newsletter'>Email/Newsletter</MenuItem>
                      <MenuItem value='Google Ads'>Google Ads</MenuItem>
                      <MenuItem value='Word of Mouth'>Word of Mouth</MenuItem>
                      <MenuItem value='Youtube'>Youtube</MenuItem>
                      <MenuItem value='Other'>Other</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {enquiryDetails ? (
              <Grid item xs={12} sm={12} md={12} xl={12} lg={12} sx={{ mt: 2 }}>
                <Controller
                  control={control}
                  name="status"
                  id="status"
                  required
                  rules={{ required: "Select status" }}
                  render={({field}) => (
                    <FormControl {...field} required fullWidth error={!!errors?.status}>
                      <InputLabel id="status-label" size="small">Status</InputLabel>
                      <Select
                        labelId="status-label"
                        id="status-select-id"
                        label="Status"
                        name="status"
                        required
                        size="small"
                        value={field.value}
                        onChange={field.onChange}
                        
                      >
                        <MenuItem value='Open'>Open</MenuItem>
                        <MenuItem value='Closed'>Closed</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            ): null}
            
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 4, mb: 2, textTransform: 'inherit' }}
                startIcon={ enquiryDetails ? <ModeEditIcon />: <ControlPointIcon />}
                color="primary"
              >
                {enquiryDetails ? 'Update' : 'Create'}
              </Button>
            </Grid>

          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
 
export default React.memo(AddEnquiry);
